import { useState, useEffect, useCallback, useMemo } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { imgProxy } from "@/lib/img";
import { toast } from "sonner";
import FavoriteButton from "@/components/dashboard/FavoriteButton";
import WhatsAppButton from "@/components/dashboard/WhatsAppButton";
import { ReviewsList } from "@/components/dashboard/Reviews";
import { FeaturedBadge } from "@/components/dashboard/ProfileCard";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Loader2, ExternalLink, MapPin, Phone, ChevronLeft, ChevronRight,
  X, ImageOff, Layers, MessageSquareQuote, PauseCircle, PlayCircle,
} from "lucide-react";

const PLACEHOLDER =
  "https://images.unsplash.com/photo-1581197326868-51cafbd1a169?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwxfHxzaWxob3VldHRlJTIwZWxlZ2FudCUyMHBvcnRyYWl0JTIwc3R1ZGlvfGVufDB8fHx8MTc4MTYzMDAzNnww&ixlib=rb-4.1.0&q=85";

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("pt-PT", { day: "2-digit", month: "short", year: "numeric" });
}
function fmtPhone(p) {
  if (!p) return "—";
  return p.replace(/(\d{3})(\d{3})(\d{3})/, "$1 $2 $3");
}

export default function ProfileDetailDialog({ profileId, open, onOpenChange }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [idx, setIdx] = useState(0);
  const [zoom, setZoom] = useState(false);
  const [agg, setAgg] = useState(null);
  const [aggLoading, setAggLoading] = useState(false);
  const [showAgg, setShowAgg] = useState(false);
  const [statusBusy, setStatusBusy] = useState(false);

  const toggleStatus = async () => {
    if (!data) return;
    const next = data.status === "inativo" ? "ativo" : "inativo";
    setStatusBusy(true);
    try {
      const res = await api.patch(`/admin/profiles/${data.id}/status`, { status: next });
      setData((d) => ({ ...d, ...res.data }));
      toast.success(next === "inativo" ? "Anúncio marcado como desativado" : "Anúncio reativado");
    } catch (e) {
      toast.error("Não foi possível alterar o estado");
    } finally {
      setStatusBusy(false);
    }
  };

  useEffect(() => {
    if (!open || !profileId) return;
    let active = true;
    (async () => {
      setLoading(true);
      setData(null);
      setIdx(0);
      setAgg(null);
      setShowAgg(false);
      try {
        const res = await api.get(`/profiles/${profileId}`);
        if (active) setData(res.data);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [open, profileId]);

  const photos = useMemo(
    () => (data?.photos && data.photos.length ? data.photos : [data?.photo_url]).filter(Boolean),
    [data]
  );
  const hasPhotos = photos.length > 0;
  const current = hasPhotos ? photos[Math.min(idx, photos.length - 1)] : PLACEHOLDER;

  const next = useCallback(() => setIdx((i) => (photos.length ? (i + 1) % photos.length : 0)), [photos.length]);
  const prev = useCallback(() => setIdx((i) => (photos.length ? (i - 1 + photos.length) % photos.length : 0)), [photos.length]);

  useEffect(() => {
    if (!zoom) return;
    const onKey = (e) => {
      if (e.key === "ArrowRight") next();
      else if (e.key === "ArrowLeft") prev();
      else if (e.key === "Escape") setZoom(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [zoom, next, prev]);

  const normEntries = data?.normalized ? Object.entries(data.normalized) : [];
  const NORM_LABELS = {
    age: "Idade", sex: "Sexo", orientation: "Orientação", height: "Altura",
    weight: "Peso", nationality: "Nacionalidade", eyes: "Olhos", hair: "Cabelo",
    ethnicity: "Etnia", bust: "Mama/Peito", languages: "Idiomas",
    services: "Serviços", price: "Preço",
  };
  const rawEntries = data?.attributes ? Object.entries(data.attributes) : [];

  const ownReviews = data?.source_comments || [];
  const loadAggregate = async () => {
    setShowAgg(true);
    if (agg || !data?.phone) return;
    setAggLoading(true);
    try {
      const res = await api.get("/comments/by-phone", { params: { phone: data.phone } });
      setAgg(res.data.items || []);
    } catch (e) {
      setAgg([]);
    } finally {
      setAggLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#18181b] border border-[#3f3f46] rounded-none text-[#fafafa] max-w-3xl max-h-[90vh] overflow-y-auto"
        data-testid="profile-detail-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-head font-normal text-lg flex items-center gap-2">
            {data?.name || "Perfil"}
            {data?.source_name && (
              <span className="text-xs text-[#71717a] font-sans">· {data.source_name}</span>
            )}
            {data?.featured_tier && <FeaturedBadge tier={data.featured_tier} testid="detail-featured" />}
            {data?.id && (
              <span className="ml-auto">
                <FavoriteButton profileId={data.id} initial={data.is_favorite} size={20} showLabel />
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        {loading && (
          <div className="flex items-center gap-2 text-sm text-[#71717a] py-16 justify-center">
            <Loader2 className="w-4 h-4 spin" /> A carregar perfil…
          </div>
        )}

        {!loading && data && (
          <div className="grid md:grid-cols-2 gap-5">
            {/* Gallery */}
            <div>
              <button
                type="button"
                onClick={() => hasPhotos && setZoom(true)}
                className="block w-full group relative"
                data-testid="profile-main-photo"
                title="Clique para ampliar"
              >
                <img
                  src={imgProxy(current)}
                  onError={(e) => (e.currentTarget.src = PLACEHOLDER)}
                  alt={data.name}
                  className="w-full aspect-[3/4] object-cover border border-[#27272a] cursor-zoom-in"
                />
                {photos.length > 1 && (
                  <span className="absolute bottom-2 right-2 text-[11px] bg-black/70 text-[#e4e4e7] px-2 py-0.5 flex items-center gap-1">
                    <Layers className="w-3 h-3" /> {idx + 1}/{photos.length}
                  </span>
                )}
                {photos.length > 1 && (
                  <>
                    <span onClick={(e) => { e.stopPropagation(); prev(); }} className="absolute left-1 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 p-1 cursor-pointer">
                      <ChevronLeft className="w-5 h-5" />
                    </span>
                    <span onClick={(e) => { e.stopPropagation(); next(); }} className="absolute right-1 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 p-1 cursor-pointer">
                      <ChevronRight className="w-5 h-5" />
                    </span>
                  </>
                )}
              </button>

              {photos.length > 1 && (
                <div className="flex gap-1.5 mt-2 flex-wrap" data-testid="profile-thumbs">
                  {photos.slice(0, 12).map((ph, i) => (
                    <button key={`${ph}-${i}`} type="button" onClick={() => setIdx(i)}
                      className={`w-12 h-12 overflow-hidden border ${i === idx ? "border-[#c8aa6e]" : "border-[#27272a]"}`}>
                      <img src={imgProxy(ph, 160)} alt="" loading="lazy" decoding="async" onError={(e) => (e.currentTarget.src = PLACEHOLDER)} className="w-full h-full object-cover bg-[#27272a]" />
                    </button>
                  ))}
                </div>
              )}
              {!hasPhotos && (
                <div className="flex items-center gap-2 text-xs text-[#71717a] mt-2">
                  <ImageOff className="w-3.5 h-3.5" /> Sem fotos recolhidas.
                </div>
              )}
            </div>

            {/* Info */}
            <div className="space-y-4 text-sm">
              <div className="flex flex-wrap gap-x-5 gap-y-1 text-[#a1a1aa] items-center">
                <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5 text-[#c8aa6e]" /> {data.city || "—"}</span>
                <span className="inline-flex items-center gap-1 font-mono"><Phone className="w-3.5 h-3.5 text-[#c8aa6e]" /> {fmtPhone(data.phone)}</span>
                {data.phone && <WhatsAppButton phone={data.phone} name={data.name} size={16} showLabel />}
              </div>

              {/* Ad lifecycle status */}
              <div
                data-testid="profile-status"
                className={`flex items-center justify-between gap-3 px-3 py-2 border ${
                  data.status === "inativo"
                    ? "border-[#ef4444]/40 bg-[#ef4444]/10"
                    : "border-[#10b981]/30 bg-[#10b981]/5"
                }`}
              >
                <div className="flex items-center gap-2 text-xs">
                  {data.status === "inativo" ? (
                    <>
                      <PauseCircle className="w-4 h-4 text-[#ef4444]" />
                      <span className="text-[#fca5a5]">
                        Anúncio desativado{data.deactivated_at ? ` · desde ${fmtDate(data.deactivated_at)}` : ""}
                      </span>
                    </>
                  ) : (
                    <>
                      <PlayCircle className="w-4 h-4 text-[#10b981]" />
                      <span className="text-[#a1a1aa]">Anúncio ativo{data.last_seen ? ` · visto ${fmtDate(data.last_seen)}` : ""}</span>
                    </>
                  )}
                </div>
                {isAdmin && (
                  <button
                    type="button"
                    data-testid="toggle-status-btn"
                    onClick={toggleStatus}
                    disabled={statusBusy}
                    className="text-[11px] border border-[#3f3f46] text-[#e4e4e7] hover:border-[#c8aa6e] hover:text-[#c8aa6e] px-2.5 py-1 transition-colors disabled:opacity-50 inline-flex items-center gap-1.5"
                  >
                    {statusBusy && <Loader2 className="w-3 h-3 spin" />}
                    {data.status === "inativo" ? "Reativar" : "Desativar"}
                  </button>
                )}
              </div>

              {normEntries.length > 0 && (
                <div className="grid grid-cols-2 gap-2" data-testid="profile-norm-attrs">
                  {normEntries.map(([k, v]) => (
                    <div key={k} className="bg-[#09090b] border border-[#27272a] px-3 py-2">
                      <div className="label-tiny">{NORM_LABELS[k] || k}</div>
                      <div className="text-[#fafafa] text-sm">{v}</div>
                    </div>
                  ))}
                </div>
              )}

              {data.description && (
                <div>
                  <div className="label-tiny mb-1">Biografia</div>
                  <p className="text-[#d4d4d8] text-[13px] leading-relaxed max-h-32 overflow-y-auto pr-1">{data.description}</p>
                </div>
              )}

              {/* Public reviews captured from the source site(s) */}
              {(() => {
                const ownCount = ownReviews.length || (data.source_comment_count || 0);
                const phoneTotal = (data.phone_comment_total ?? ownCount) || 0;
                const otherSites = Math.max(phoneTotal - ownCount, 0);
                // Nothing anywhere for this ad/number and the site doesn't publish
                // reviews → don't render the block at all.
                if (phoneTotal === 0 && !data.has_reviews) return null;
                return (
                <div data-testid="profile-reviews">
                  <div className="label-tiny mb-1 flex items-center gap-1.5">
                    <MessageSquareQuote className="w-3.5 h-3.5 text-[#c8aa6e]" />
                    Comentários do site {data.source_name ? `(${data.source_name})` : ""}
                    {ownCount > 0 && <span className="text-[#c8aa6e]">· {ownCount}</span>}
                  </div>
                  {ownCount > 0 ? (
                    <div className="max-h-56 overflow-y-auto pr-1">
                      <ReviewsList items={ownReviews} empty="Sem comentários neste anúncio." />
                    </div>
                  ) : (
                    <p className="text-xs text-[#71717a]" data-testid="no-reviews-note">
                      Sem comentários neste anúncio.
                    </p>
                  )}
                  {otherSites > 0 && !showAgg && (
                    <button
                      type="button"
                      onClick={loadAggregate}
                      data-testid="load-aggregate-comments"
                      className="mt-2 text-xs text-[#c8aa6e] hover:text-[#d4b982] inline-flex items-center gap-1"
                    >
                      <Layers className="w-3.5 h-3.5" /> Ver {otherSites} comentário{otherSites > 1 ? "s" : ""} deste número noutros sites
                    </button>
                  )}
                  {showAgg && (
                    <div className="mt-3 border-t border-[#27272a] pt-3">
                      <div className="label-tiny mb-1">Todos os sites (por número)</div>
                      {aggLoading ? (
                        <div className="flex items-center gap-2 text-xs text-[#71717a] py-2">
                          <Loader2 className="w-3.5 h-3.5 spin" /> A reunir comentários…
                        </div>
                      ) : (
                        <div className="max-h-56 overflow-y-auto pr-1">
                          <ReviewsList items={agg} showSource empty="Nenhum comentário encontrado noutros sites." />
                        </div>
                      )}
                    </div>
                  )}
                </div>
                );
              })()}

              {rawEntries.length > 0 && (
                <div>
                  <div className="label-tiny mb-1">Todos os campos do anúncio</div>
                  <table className="w-full text-[13px]">
                    <tbody>
                      {rawEntries.map(([k, v]) => (
                        <tr key={k} className="border-b border-[#27272a]/60">
                          <td className="py-1 pr-3 text-[#71717a] align-top whitespace-nowrap">{k}</td>
                          <td className="py-1 text-[#e4e4e7]">{v}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="flex items-center justify-between border-t border-[#27272a] pt-3 text-xs text-[#71717a]">
                <span>1.º registo: {fmtDate(data.first_seen)}</span>
                {data.profile_url && (
                  <a href={data.profile_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[#c8aa6e] hover:text-[#d4b982]">
                    <ExternalLink className="w-3.5 h-3.5" /> Anúncio original
                  </a>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Fullscreen zoom overlay */}
        {zoom && hasPhotos && (
          <div
            className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center"
            onClick={() => setZoom(false)}
            data-testid="profile-zoom"
          >
            <button onClick={() => setZoom(false)} className="absolute top-4 right-4 text-white/80 hover:text-white">
              <X className="w-7 h-7" />
            </button>
            <span onClick={(e) => { e.stopPropagation(); prev(); }} className="absolute left-4 text-white/70 hover:text-white cursor-pointer">
              <ChevronLeft className="w-9 h-9" />
            </span>
            <img
              src={imgProxy(current)}
              onError={(e) => (e.currentTarget.src = PLACEHOLDER)}
              alt=""
              className="max-h-[88vh] max-w-[88vw] object-contain"
              onClick={(e) => e.stopPropagation()}
            />
            <span onClick={(e) => { e.stopPropagation(); next(); }} className="absolute right-4 text-white/70 hover:text-white cursor-pointer">
              <ChevronRight className="w-9 h-9" />
            </span>
            <span className="absolute bottom-4 text-white/80 text-sm">{idx + 1} / {photos.length}</span>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
